// ORG-1197: trivial bump to trigger a fresh Kargo build
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import WalletWorkAuthorizationSummary from '../components/profile/WalletWorkAuthorizationSummary';
import WalletRightToWork from '../components/profile/WalletRightToWork';
import WalletCredentials from '../components/profile/WalletCredentials';
import WalletAlert from '../components/profile/WalletAlert';
import { fetchCountryWorkProfile } from '../actions/countryWorkProfile';
import usWorkCardStyle from '../style/components/profile/USWorkSectionCard.module.scss';

const WalletUsWorkProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isFetching, error, present } = useSelector(
    (state) => state.countryWorkProfile,
  );

  // Mount-only fetch, matching WalletEmails.jsx's shape: deps on [dispatch]
  // only, so this runs once on mount, not every time isFetching flips.
  // WalletProfile already fetches this to gate the tab, which populates the
  // same slice — so only fetch here when presence hasn't been resolved yet
  // (present === null), avoiding a duplicate request each time the tab opens.
  useEffect(() => {
    if (!isFetching && present === null) {
      dispatch(fetchCountryWorkProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleRetryFetch = useCallback(() => {
    dispatch(fetchCountryWorkProfile());
  }, [dispatch]);

  // A failed fetch must not render the three cards below with whatever's left
  // in state (initial nulls/[] on a first-load failure, or stale data on a
  // later one) — that reads as "not authorized, no credentials on file"
  // rather than "we couldn't load this." Show the error + retry instead, and
  // let the cards return once a retry succeeds and `error` clears.
  if (error) {
    return (
      <div className={usWorkCardStyle.fetchErrorAlert}>
        <WalletAlert variant="error">
          <p className={usWorkCardStyle.fetchErrorBody}>
            {t('profile.usWorkProfileFetchErrorBody')}
          </p>
          <button
            type="button"
            data-testid="us-work-profile-fetch-retry"
            className={usWorkCardStyle.fetchErrorRetry}
            onClick={handleRetryFetch}
          >
            {t('profile.usWorkProfileFetchErrorRetry')}
          </button>
        </WalletAlert>
      </div>
    );
  }

  return (
    <>
      <WalletWorkAuthorizationSummary />
      <WalletRightToWork />
      <WalletCredentials />
    </>
  );
};

export default WalletUsWorkProfile;
